/**
 * WaitlistAdminPage — admin-only review + approval for beta waitlist entries.
 *
 * Flow:
 *   1. Admin sees a list of pending register-interest submissions
 *   2. Clicks "Approve" on an entry
 *   3. Backend provisions a real user (own household, random temp password,
 *      TOTP secret) and returns the provisioning URI + temp password
 *   4. Admin forwards both to the approved user out-of-band (email/Signal)
 *
 * The approval result modal shows the temp password, otpauth:// URI, and a
 * scannable QR code (rendered via Google Charts so no new dependency) so
 * the admin can forward them in one shot.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  UserCheck, RefreshCw, Copy, Check, X, AlertCircle, CheckCircle2,
  Mail, Building2, Clock, Shield,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

interface WaitlistEntry {
  id: number
  created_at: string | null
  email: string
  name: string
  company: string | null
  message: string | null
  status: 'pending' | 'approved' | 'rejected'
  approved_at: string | null
  notes: string | null
}

interface ApprovalResult {
  user_id: string
  email: string
  name: string
  role: string
  provisioning_uri: string
  detail: string
}

function parseTempPassword(detail: string): string | null {
  const match = /out-of-band:\s+(\S+)/.exec(detail)
  return match ? match[1] : null
}

function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* ignore */
    }
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      className="btn-secondary text-xs gap-1.5"
      aria-label={label}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? 'Copied' : label}
    </button>
  )
}

export default function WaitlistAdminPage() {
  const user = useAuth(s => s.user)
  const isAdmin = user?.is_platform_admin === true
  const [entries, setEntries] = useState<WaitlistEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [approvingId, setApprovingId] = useState<number | null>(null)
  const [approvalResult, setApprovalResult] = useState<ApprovalResult | null>(null)
  const [filter, setFilter] = useState<'pending' | 'all'>('pending')

  const fetchWaitlist = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get('/api/auth/waitlist')
      setEntries(Array.isArray(data) ? data : [])
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load waitlist')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isAdmin) fetchWaitlist()
  }, [isAdmin])

  const approve = async (entry: WaitlistEntry) => {
    if (!window.confirm(`Approve ${entry.name} (${entry.email}) as a beta user?`)) return
    setApprovingId(entry.id)
    try {
      const { data } = await api.post(`/api/auth/waitlist/${entry.id}/approve`)
      setApprovalResult(data)
      await fetchWaitlist()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Approval failed')
    } finally {
      setApprovingId(null)
    }
  }

  const visible = useMemo(
    () =>
      filter === 'pending'
        ? entries.filter(e => e.status === 'pending')
        : entries,
    [entries, filter],
  )

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 }
    for (const e of entries) {
      if (e.status in c) c[e.status]++
    }
    return c
  }, [entries])

  if (!isAdmin) {
    return (
      <div className="card p-6 text-center">
        <Shield size={28} className="text-text-muted mx-auto mb-3" />
        <h2 className="text-sm font-semibold text-text-primary">Admin access required</h2>
        <p className="text-xs text-text-muted mt-1">
          The waitlist is only visible to admin users.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <UserCheck size={20} className="text-blue-light" aria-hidden />
            Beta Waitlist
          </h1>
          <p className="text-sm text-text-secondary">
            Review and approve people who have registered their interest. Approving
            provisions a real user account with an isolated household.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchWaitlist}
            className="btn-secondary text-xs gap-1.5"
            disabled={loading}
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-4 stagger-children">
        <button
          type="button"
          onClick={() => setFilter('pending')}
          className={clsx(
            'stat-card text-left transition-all',
            filter === 'pending' && 'ring-2 ring-blue',
          )}
        >
          <p className="stat-label">Pending</p>
          <p className="stat-value text-2xl">{counts.pending}</p>
          <p className="text-[10px] text-text-muted mt-1">Awaiting review</p>
        </button>
        <div className="stat-card">
          <p className="stat-label">Approved</p>
          <p className="stat-value text-2xl text-emerald">{counts.approved}</p>
          <p className="text-[10px] text-text-muted mt-1">Now live beta users</p>
        </div>
        <button
          type="button"
          onClick={() => setFilter('all')}
          className={clsx(
            'stat-card text-left transition-all',
            filter === 'all' && 'ring-2 ring-blue',
          )}
        >
          <p className="stat-label">All entries</p>
          <p className="stat-value text-2xl">{entries.length}</p>
          <p className="text-[10px] text-text-muted mt-1">Show all statuses</p>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-rose/30 bg-rose/5 text-sm text-rose-light flex items-start gap-2">
          <AlertCircle size={14} className="shrink-0 mt-0.5" aria-hidden />
          {error}
        </div>
      )}

      {/* Entries */}
      {loading && entries.length === 0 ? (
        <div className="space-y-3">
          {[0, 1, 2].map(i => (
            <div key={i} className="h-20 skeleton" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="card p-10 text-center">
          <UserCheck size={32} className="text-text-muted mx-auto mb-3" />
          <p className="text-sm text-text-primary font-medium">
            {filter === 'pending' ? 'No pending requests' : 'No entries yet'}
          </p>
          <p className="text-xs text-text-muted mt-1">
            {filter === 'pending'
              ? "When someone registers their interest, they'll appear here."
              : 'The waitlist is empty.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map(entry => (
            <div
              key={entry.id}
              className={clsx(
                'card hover-lift p-4 flex flex-col sm:flex-row gap-3 sm:items-center',
                entry.status === 'approved' && 'opacity-70',
              )}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-text-primary">{entry.name}</p>
                  <span
                    className={clsx(
                      'text-[10px] uppercase tracking-wider',
                      entry.status === 'pending' && 'badge-amber',
                      entry.status === 'approved' && 'badge-green',
                      entry.status === 'rejected' && 'badge-red',
                    )}
                  >
                    {entry.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-text-muted mt-1 flex-wrap">
                  <span className="flex items-center gap-1">
                    <Mail size={11} aria-hidden />
                    {entry.email}
                  </span>
                  {entry.company && (
                    <span className="flex items-center gap-1">
                      <Building2 size={11} aria-hidden />
                      {entry.company}
                    </span>
                  )}
                  {entry.created_at && (
                    <span className="flex items-center gap-1">
                      <Clock size={11} aria-hidden />
                      {new Date(entry.created_at).toLocaleString('en-GB', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </span>
                  )}
                </div>
                {entry.message && (
                  <p className="text-xs text-text-secondary mt-2 pl-2 border-l-2 border-border italic">
                    {entry.message}
                  </p>
                )}
              </div>
              {entry.status === 'pending' && (
                <div className="shrink-0">
                  <button
                    type="button"
                    onClick={() => approve(entry)}
                    disabled={approvingId === entry.id}
                    className="btn-primary text-xs gap-1.5"
                  >
                    {approvingId === entry.id ? (
                      <RefreshCw size={13} className="animate-spin" aria-hidden />
                    ) : (
                      <Check size={13} aria-hidden />
                    )}
                    {approvingId === entry.id ? 'Approving…' : 'Approve'}
                  </button>
                </div>
              )}
              {entry.status === 'approved' && entry.approved_at && (
                <div className="shrink-0 text-xs text-text-muted">
                  Approved{' '}
                  {new Date(entry.approved_at).toLocaleDateString('en-GB', {
                    dateStyle: 'medium',
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Approval result modal */}
      {approvalResult && (
        <ApprovalResultModal
          result={approvalResult}
          onClose={() => setApprovalResult(null)}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Approval result modal — shows temp password + TOTP URI + QR
// ─────────────────────────────────────────────────────────────────────────────

function ApprovalResultModal({
  result,
  onClose,
}: {
  result: ApprovalResult
  onClose: () => void
}) {
  const tempPassword = parseTempPassword(result.detail)
  // QR code rendered via Google Charts — no extra dependency.
  const qrUrl = `https://chart.googleapis.com/chart?cht=qr&chs=220x220&chl=${encodeURIComponent(result.provisioning_uri)}&choe=UTF-8`

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="card p-6 w-full max-w-lg relative max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1 text-text-muted hover:text-text-primary"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-emerald/10 border border-emerald/30 flex items-center justify-center shrink-0">
            <CheckCircle2 size={20} className="text-emerald" aria-hidden />
          </div>
          <div>
            <h2 className="text-base font-bold text-text-primary">User approved</h2>
            <p className="text-xs text-text-muted mt-0.5">
              Forward these credentials to {result.name} out-of-band (email, Signal,
              etc). They expire when reset — send once.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Identity */}
          <div className="p-3 rounded-lg bg-surface-hover/50 border border-border">
            <p className="text-[10px] uppercase tracking-wider text-text-muted">Account</p>
            <p className="text-sm font-semibold text-text-primary mt-0.5">{result.name}</p>
            <p className="text-xs text-text-secondary">{result.email}</p>
            <p className="text-[10px] text-text-muted mt-1 font-mono">{result.user_id}</p>
          </div>

          {/* Temp password */}
          {tempPassword && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-medium text-text-secondary">
                  Temporary password
                </p>
                <CopyButton value={tempPassword} />
              </div>
              <div className="p-2.5 rounded bg-base border border-border font-mono text-sm text-text-primary break-all select-all">
                {tempPassword}
              </div>
              <p className="text-[10px] text-text-muted mt-1.5">
                The user should change this on first login.
              </p>
            </div>
          )}

          {/* TOTP QR */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-medium text-text-secondary">
                TOTP QR code
              </p>
              <CopyButton value={result.provisioning_uri} label="Copy URI" />
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 p-3 rounded bg-white border border-border">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={qrUrl}
                alt="TOTP QR code"
                width={220}
                height={220}
                className="shrink-0"
              />
              <p className="text-[10px] text-slate-700 break-all font-mono">
                {result.provisioning_uri}
              </p>
            </div>
            <p className="text-[10px] text-text-muted mt-1.5">
              Send this to the user so they can scan it into Google Authenticator,
              Authy, 1Password, etc.
            </p>
          </div>

          {/* Compose email helper */}
          {tempPassword && (
            <div>
              <p className="text-xs font-medium text-text-secondary mb-1.5">
                Ready-to-send message
              </p>
              <textarea
                readOnly
                rows={6}
                aria-label="Ready-to-send welcome message"
                className="input w-full text-xs font-mono resize-y"
                value={`Hi ${result.name.split(' ')[0]},

You're approved for the FinanceGenie beta!

Login: https://financegenie.bullers.com/login
Email: ${result.email}
Temporary password: ${tempPassword}

Scan this into your authenticator app (Google Authenticator, Authy, 1Password):
${result.provisioning_uri}

After you log in the first time, please change your password in Settings.

— Simon`}
              />
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary text-xs">
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
